import styled, { keyframes } from 'styled-components'

const TEAL = '#0f6e52'
const TEAL_DEEP = '#0a4d3a'
const TEAL_LIGHT = '#14916b'
const TEAL_SOFT = '#e6f4ee'
const GRAY_LINE = '#e0e8e4'
const GRAY_TEXT = '#566b62'
const GRAY_DARK = '#23332c'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ---------- card da seção (embutido no dashboard, sempre visível) ----------- */
export const Section = styled.section`
  width: 100%;
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  animation: ${fadeIn} 0.3s ease both;
`

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-bottom: 1px solid ${GRAY_LINE};
  background: ${TEAL_SOFT};

  @media (max-width: 768px) { padding: 14px; }
`

export const SectionTitle = styled.h2`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 800;
  color: ${TEAL_DEEP};
  letter-spacing: -0.2px;

  @media (max-width: 768px) { font-size: 15px; }
`

export const SectionSub = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${TEAL};
  white-space: nowrap;
`

/* ---------- corpo ------------------------------------------------------------ */
export const Body = styled.div`
  padding: 16px 20px 22px;

  @media (max-width: 768px) { padding: 12px 10px 18px; }
`

export const Loading = styled.div`
  padding: 60px 0;
  text-align: center;
  color: ${GRAY_TEXT};
  font-size: 14px;
`

/* ---------- seletor de matéria (chips roláveis) ----------------------------- */
export const ChipsWrap = styled.div`
  display: flex;
  gap: 7px;
  overflow-x: auto;
  padding: 2px 2px 8px;
  margin-bottom: 4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  /* esconde a barra no mobile, mantendo o scroll por toque */
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${GRAY_LINE}; border-radius: 4px; }
`

export const Chip = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? TEAL : GRAY_LINE)};
  background: ${({ $active }) => ($active ? TEAL : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--surface)' : GRAY_DARK)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  min-height: 42px;   /* alvo de toque confortável no dedo */

  &:hover {
    border-color: ${TEAL_LIGHT};
    background: ${({ $active }) => ($active ? TEAL : TEAL_SOFT)};
  }
`

/* pontinho de tendência dentro do chip */
export const ChipDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'up' ? '#1d8b5a' : $status === 'down' ? 'var(--red, #dc2626)' : '#b7791f'};
`

/* ---------- painel do gráfico ------------------------------------------------ */
export const ChartPanel = styled.div`
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 14px 10px 8px;
  background: var(--surface);

  @media (max-width: 768px) { padding: 12px 2px 6px; }
`

export const ChartHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 8px 10px;
`

export const ChartTitle = styled.h3`
  margin: 0;
  font-size: 15.5px;
  font-weight: 800;
  color: ${GRAY_DARK};
  letter-spacing: -0.2px;
`

/* selo de tendência (subiu / caiu / estável) */
export const TrendBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $status }) => {
    if ($status === 'up') return `color:#1d8b5a; background:rgba(29,139,90,.12);`
    if ($status === 'down') return `color:var(--red, #dc2626); background:rgba(192,57,43,.10);`
    return `color:#b7791f; background:rgba(200,138,4,.14);`
  }}
`

export const ChartArea = styled.div`
  width: 100%;
  height: 260px;

  @media (max-width: 768px) { height: 230px; }
  @media (max-width: 400px) { height: 210px; }
`

/* ---------- tooltip customizado ---------------------------------------------- */
export const TipBox = styled.div`
  background: ${TEAL_DEEP};
  color: #fff;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);

  strong { font-size: 15px; }
`

export const TipLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
  margin-bottom: 2px;
`

export const TipMeta = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  opacity: 0.85;
`

/* ---------- resumo das demais matérias --------------------------------------- */
export const SummaryTitle = styled.h4`
  margin: 20px 0 10px;
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${TEAL};
`

export const SummaryList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (max-width: 520px) { grid-template-columns: 1fr; }
`

export const SummaryItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 11px 13px;
  border: 1px solid ${({ $active }) => ($active ? TEAL_LIGHT : GRAY_LINE)};
  border-radius: 11px;
  background: ${({ $active }) => ($active ? TEAL_SOFT : 'var(--surface)')};
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
  min-height: 46px;

  &:hover { border-color: ${TEAL_LIGHT}; background: ${TEAL_SOFT}; }
`

export const SummaryName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${GRAY_DARK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const SummaryRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
`

/* último valor da matéria (nota % ou conceito) */
export const SummaryValue = styled.span`
  font-size: 13.5px;
  font-weight: 800;
  color: ${({ $tone }) =>
    $tone === 'up' ? '#1d8b5a' : $tone === 'down' ? 'var(--red, #dc2626)' : GRAY_TEXT};
`

/* mini-sparkline embutida no card de resumo */
export const Spark = styled.svg`
  width: 46px;
  height: 20px;
  flex-shrink: 0;
  overflow: visible;
`

/* ---------- legenda / nota de rodapé ----------------------------------------- */
export const FootNote = styled.p`
  margin: 16px 0 0;
  font-size: 11.5px;
  line-height: 1.55;
  color: ${GRAY_TEXT};
  text-align: center;
`